import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useScreenTime } from "@/hooks/useScreenTime";
import { Download, Mail } from "lucide-react";
import { useAppSetting, getBrandingUrl } from "@/hooks/useAppSettings";
import { useText } from "@/contexts/AppConfigContext";
import defaultLogo from "@/assets/app-icon.png";
import InstallGuideModal from "@/components/InstallGuideModal";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, type SupportedLanguage } from "@/i18n";
import { Botao, Campo, Chips } from "@/components/sistema";
import type { Chip } from "@/components/sistema";

/**
 * Entrar. A primeira tela do app, e para muita gente a única que dá problema.
 *
 * O que mudou, e por quê:
 *
 * - O fundo eram dois gradientes radiais escritos na mão. Fundo é papel.
 * - O botão de entrar era `hsl(var(--sem-amarillo))`, uma cor do semáforo
 *   glicêmico usada como cor de ação. Semáforo é glicemia; ação é latão.
 * - O cartão tinha uma sombra grande com a cor do preto escrita na mão.
 *   Virou fio de 1px.
 * - Os três idiomas eram pílulas de 44×44px lado a lado. Viraram `Escolha`
 *   empilhada: linha cheia, 72px, tique no selecionado. Quem abriu o app no
 *   idioma errado precisa achar isso de primeira.
 * - O campo de e-mail tinha alvo de ~52px e texto de 16px; virou `Campo`
 *   com `tipo="email"` e alvo de 56px. Os erros abaixo dele eram
 *   `text-red-500`; agora é a tinta destrutiva do tema.
 * - O rodapé legal estava em 11px. Na régua o piso é 17px.
 *
 * Nada da autenticação mudou: mesmo `login(email)` do AuthContext, mesma
 * validação de formato, mesmo `?redirect=`, mesmo `defaultRedirect`, mesma
 * re-tradução do erro por `errorKey` na tela travada em um idioma, mesmo
 * fluxo de instalação do PWA.
 */

interface LoginProps {
  /**
   * Renders this screen in a fixed language and hides the language selector.
   * Used by product namespaces that are sold in a single market. Omitted
   * everywhere else, which keeps the shared login exactly as it was.
   */
  lockedLocale?: SupportedLanguage;
  /** Where to land after signing in when no ?redirect= is present. */
  defaultRedirect?: string;
}

const Login = ({ lockedLocale, defaultRedirect = "/" }: LoginProps) => {
  useScreenTime("login");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t: tCurrent, i18n } = useTranslation();
  const { language, setLanguage } = useLocale();

  // getFixedT translates in the locked language without changing the app's
  // language, so visiting this screen never rewrites the user's preference.
  const t = lockedLocale ? i18n.getFixedT(lockedLocale) : tCurrent;
  const { canInstall, install } = usePWAInstall();
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const { data: logoPath } = useAppSetting("logo");
  const logoUrl = getBrandingUrl(logoPath) || defaultLogo;
  const appName = useText("app_name");

  const handleInstallClick = async () => {
    if (canInstall) {
      await install();
    } else {
      setShowInstallGuide(true);
    }
  };

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError(t("login.email_invalid"));
      return;
    }
    setSubmitting(true);
    setError("");
    const result = await login(email);
    setSubmitting(false);
    if (result.error) {
      // result.error arrives translated in the app's global language; on a
      // locked screen re-translate from the key so the message matches the
      // rest of the page.
      setError(result.errorKey ? t(result.errorKey) : result.error);
    } else {
      const redirectTo = searchParams.get("redirect") || defaultRedirect;
      navigate(redirectTo, { replace: true });
    }
  };

  const OPCOES_IDIOMA: Chip<SupportedLanguage>[] = SUPPORTED_LANGUAGES.map((lang) => ({
    valor: lang,
    rotulo: LANGUAGE_LABELS[lang],
  }));

  return (
    <div
      className="flex min-h-screen flex-col justify-center bg-background"
      style={{ paddingTop: 32, paddingBottom: 32 }}
    >
      <div className="mx-auto w-full" style={{ maxWidth: 420 }}>
        {/* A marca em cima, grande, no quadrado com fio de ouro da home. */}
        <div className="flex flex-col items-center">
          <img
            src={logoUrl}
            alt={appName}
            className="object-cover"
            style={{ width: 112, height: 112, borderRadius: 32, boxShadow: "0 0 0 2px hsl(var(--accent))" }}
          />
          <h1
            className="font-bold leading-tight"
            style={{ fontSize: "var(--t-tela)", paddingTop: 16 }}
          >
            {appName}
          </h1>
          <p className="text-center leading-snug" style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)", paddingTop: 6, paddingBottom: 24 }}>
            {t("login.subtitle")}
          </p>
        </div>

        {/* Idioma antes de tudo: a tela e o app depois dela seguem esta
            escolha. Escondido quando a tela está travada em um idioma. */}
        {!lockedLocale && (
          <div style={{ paddingBottom: 20 }}>
            <Chips itens={OPCOES_IDIOMA} valor={language} onEscolher={setLanguage} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Campo
            rotulo={t("login.email_label", "Email")}
            valor={email}
            onMudar={(v) => { setEmail(v); setError(""); }}
            placeholder={t("login.email_placeholder")}
            icone={Mail}
            tipo="email"
            inputMode="email"
            autoFocus
          />

          {error && (
            <p
              className="leading-snug"
              style={{
                padding: "12px var(--gutter) 0",
                fontSize: "var(--t-corpo)",
                color: "hsl(var(--destructive))",
              }}
            >
              {error}
            </p>
          )}

          <div style={{ padding: "20px var(--gutter) 0" }}>
            {/* Sem ícone de espera: o `Botao` não gira o ícone, e um ícone de
                carregamento parado parece um app travado. O rótulo já diz. */}
            <Botao
              type="submit"
              rotulo={submitting ? t("login.loading") : t("login.button")}
              desabilitado={submitting}
            />
          </div>

          {/* Instalar abre um modal, então nunca pode parecer a ação da tela. */}
          <div style={{ padding: "12px var(--gutter) 0" }}>
            <Botao
              rotulo={t("login.install_button")}
              icone={Download}
              variante="secundaria"
              onClick={handleInstallClick}
            />
          </div>
        </form>

        <p
          className="text-center leading-relaxed"
          style={{
            padding: "28px var(--gutter) 0",
            fontSize: "var(--t-apoio)",
            color: "var(--tinta-apoio)",
          }}
        >
          <Link to="/terms-of-service" className="underline underline-offset-2">
            {t("settings.terms_of_service")}
          </Link>
          {" · "}
          <Link to="/privacy-policy" className="underline underline-offset-2">
            {t("settings.privacy_policy")}
          </Link>
        </p>
      </div>

      <InstallGuideModal
        open={showInstallGuide}
        onOpenChange={setShowInstallGuide}
        onNativeInstall={handleInstallClick}
      />
    </div>
  );
};

export default Login;
